import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Download } from "react-feather";
import { useNavigate, useParams, useLocation, useOutletContext } from "react-router-dom";
import { embedDashboard } from "amazon-quicksight-embedding-sdk";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { LoadingOver } from "../../../components/LoadingOver";
import { Colors } from "../../../values/colors";
import { Text } from "../../../components/BaseComponents/styles";
import { StatusBadge } from "../styles";
import Stats from "../../../services/Stats";
import { DownloadReportModal } from "../components/DownloadReportModal";
import { SurveyFiltersPanel } from "../components/SurveyFiltersPanel";
import { AppliedFiltersBar } from "../components/AppliedFiltersBar";
import { BackBtn, EmbedContent, DownloadBackdrop } from "./styles";

const STATUS_BG = {
  inProgress: { label: "Em andamento", color: Colors.orange },
  finalized: { label: "Finalizada", color: Colors.secondaryGreen },
  stopped: { label: "Pausada", color: Colors.mediumGray },
  closed: { label: "Fechada", color: Colors.blueGray },
  scheduled: { label: "Agendada", color: Colors.primaryPurple },
};

const NO_FILTERS = { businessUnit: null, sector: null, subsector: null };

const formatDate = (isoString) => {
  if (!isoString) return undefined;
  const [year, month, day] = isoString.split("T")[0].split("-");
  return `${year}/${month}/${day}`;
};

export const SurveyDetail = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [setOptions] = useOutletContext();
  const survey = state?.survey;

  const [activeTab] = useState("psychosocial");
  const [loading, setLoading] = useState(false);
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [panelAnchor, setPanelAnchor] = useState({ right: 16 });
  const [appliedFilters, setAppliedFilters] = useState(NO_FILTERS);
  const embedContainer = useRef();
  const dashboard = useRef();

  const getData = async () => {
    setLoading(true);
    try {
      const data = await Stats.getQSEmbed(activeTab, "single");
      embedContainer.current.innerHTML = "";
      dashboard.current = embedDashboard({
        url: data.embed_url,
        container: embedContainer.current,
        parameters: {
          DataInicioMapeamento: formatDate(survey?.start_at),
          DataFimMapeamento: formatDate(survey?.end_at),
          TotalUsuariosAtivos: survey?.activeUsers,
          SurveyId: id,
          BusinessId: data.business_id,
          ...(appliedFilters?.businessUnit && {
            BusinessUnit: appliedFilters.businessUnit,
          }),
          ...(appliedFilters?.sector && {
            Setor: appliedFilters.sector,
          }),
          ...(appliedFilters?.subsector && {
            Subsetor: appliedFilters.subsector,
          }),
        },
        scrolling: "no",
        errorCallback: () => setLoading(false),
        loadCallback: () => setLoading(false),
        height: "AutoFit",
        loadingHeight: "750px",
        width: "100%",
        locale: "pt-BR",
        footerPaddingEnabled: true,
        undoRedoDisabled: true,
        resetDisabled: true,
      });
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [activeTab, appliedFilters]);

  const handleClearAppliedFilters = () => setAppliedFilters(NO_FILTERS);

  const handleRemoveFilterLevel = (levels) => {
    setAppliedFilters((prev) => {
      const next = { ...prev };
      levels.forEach((level) => { next[level] = null; });
      return next;
    });
  };

  const statusInfo = STATUS_BG[survey?.status] || {};

  useEffect(() => {
    setOptions({
      start: (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <BackBtn onClick={() => navigate('/psciossocial-risk')}>
            <ArrowLeft size={18} color="#fff" />
          </BackBtn>
          <Text color="white" weight={700}>
            {survey?.title || "Pesquisa"}
          </Text>
        </div>
      ),
      middle: statusInfo.label ? (
        <div style={{
          display: "flex-row",
          alignItems: "center",
          gap: 8,
          }}>
          <Text color="white" size={13} style={{ margin: '0 15px' }}>Status:</Text>
          <StatusBadge bg={statusInfo.color}>{statusInfo.label}</StatusBadge>
        </div>
      ) : null,
      end: [
        <SurveyFiltersPanel
          key="filters"
          surveyId={id}
          appliedFilters={appliedFilters}
          onFilter={setAppliedFilters}
        />,
        <Button
          key="download"
          color={Colors.primaryPurple}
          icon={<Download size={16} />}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setPanelAnchor({ top: rect.top, right: window.innerWidth - rect.left });
            setShowDownloadPanel((prev) => !prev);
          }}
          rounded
        >
          Download Relatório
        </Button>,
      ],
    });
    return () => setOptions(null);
  }, [survey, navigate, setOptions, setPanelAnchor, setShowDownloadPanel, appliedFilters, id, statusInfo.color, statusInfo.label]);

  return (
    <>
      <AppliedFiltersBar
        filters={appliedFilters}
        onRemove={handleRemoveFilterLevel}
        onClear={handleClearAppliedFilters}
      />

      <Box noPadding noHeader style={{ minHeight: "80vh" }}>
        <EmbedContent>
          {loading && <LoadingOver />}
          <div ref={embedContainer} />
        </EmbedContent>
      </Box>

      {showDownloadPanel && (
        <>
          <DownloadBackdrop onClick={() => setShowDownloadPanel(false)} />
          <DownloadReportModal
            survey={survey}
            onClose={() => setShowDownloadPanel(false)}
            style={{
              position: "fixed",
              top: panelAnchor.top,
              right: panelAnchor.right,
              left: "auto",
              zIndex: 200,
            }}
          />
        </>
      )}
    </>
  );
};
